import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Fab,
    IconButton,
    InputAdornment,
    Snackbar,
    TextField,
    Tooltip,
    Menu,
    MenuItem,
    Checkbox,
    Typography,
    Chip, Button, Stack,
} from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useNavigate, useParams } from 'react-router-dom';
import {Chat, Course, CoursePlan, Message} from '../types/courseTypes';
import MessageArray from '../components/MessageArray';
import {
    addMessage,
    deleteMessage,
    fetchCourseInfo,
    fetchMessage,
    fetchCourse,
    addChatCategories, fetchLLMs, fetchCourseChats, updateMessage
} from '../services/MentorApiService';
import SendIcon from '@mui/icons-material/Send';
import {llmList} from "./llmList";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {useSnackbar} from "./DashBoard";
import localization from "../locals/en.json";

const SuggestionPage = () => {
    const courseId = useParams<{ id: string }>().id!;
    const chatId = useParams<{ chat_id: string }>().chat_id!;
    const navigate = useNavigate();
    const [course, setCourse] = useState<Course | null>(null);
    const [coursePlan, setCoursePlan] = useState<CoursePlan | null>(null);
    const [chat, setChat] = useState<Chat | null>(null);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState<number[]>([]);
    const [llms, setLlms] = useState<{ id: number; name: string }[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedSailCategoryIds, setSelectedSailCategoryIds] = useState<number[]>([]);
    const [confirmedCategories, setConfirmedCategories] = useState<boolean>(false);
    const { showSnackbar } = useSnackbar();
    const [evaluationChatId, setEvaluationChatId] = useState<number>(0);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [selectedLlm, setSelectedLlm] = useState<number>(3);
    const EVALUATION_MODE_ID = 3;

    const sailCategories = [
        { id: 1, name: 'Impacts of AI', description: 'Exploring AI\'s societal impacts by identifying human-AI interactions, understanding ethical use, assessing AI adoption and potential harms, and demonstrating data literacy and interdisciplinary applications.' },
        { id: 2, name: 'What AI is & How it Works', description: 'Mastering AI fundamentals by defining key terms, comparing technologies, understanding data use, explaining human language interaction, and exploring advanced concepts like deep learning and quantum computing.' },
        { id: 3, name: 'Cognitive Skills', description: 'Developing cognitive skills by assessing AI tool suitability, understanding its impact on work and creativity, and demonstrating computational thinking and AI model-building expertise.' },
        { id: 4, name: 'Applied Skills', description: 'Enhancing applied skills by selecting and using appropriate AI tools to collaborate, communicate, solve problems, and perform tasks in specific contexts such as learning and research.' },
        { id: 5, name: 'Social, Cultural & Ethical Issues', description: 'Exploring social and ethical issues of AI by assessing societal benefits, identifying ethical implications, discussing cultural impacts, and applying principles-based approaches to equity, inclusion, and policy development.' },
        { id: 6, name: 'Risks & Mitigations', description: 'Addressing the challenges of AI by identifying and mitigating risks, promoting responsible use, ensuring inclusivity, and applying moral frameworks to assess AI\'s societal value and future directions.' },
    ];

    const tempInitialMessage: Message = {
        id: 0,
        chatId: parseInt(chatId, 10),
        inputText: "Please provide suggestions on how to embed AI literacy concepts from the SAIL framework into my course design.",
        responseText: "",
        createdAt: new Date(),
    };

    const generateTempId = () => Math.floor(Math.random() * -1000000);

    const useFadeTransition = (trigger: boolean, messagesLength: number, duration: number = 1000) => {
        const [isVisible, setIsVisible] = useState(messagesLength === 0 && !trigger);

        useEffect(() => {
            setIsVisible(messagesLength === 0 && !trigger);
        }, [trigger, messagesLength]);

        const transitionStyles = {
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
            transition: `all ${duration}ms ease-in-out`,
        };

        return { isVisible, transitionStyles };
    };

    const { isVisible, transitionStyles } = useFadeTransition(confirmedCategories, messages.length);

    const handleFetchMessages = () => {
        fetchMessage(parseInt(courseId, 10), parseInt(chatId, 10))
            .then((response) => {
                setMessages(response.data.messages);
            })
            .catch((error) => {
                showSnackbar('Failed to fetch chat messages', 'error');
            });
    };

    const handleFetchLLMs = () => {
        fetchLLMs()
            .then((response) => {
                setLlms(response.data.llms);
            })
            .catch((error) => {
                showSnackbar('Failed to fetch LLMs', 'error');
            });
    };

    const handleFetchConversation = () => {
        fetchCourse(parseInt(courseId, 10))
            .then((response) => {
                setCourse(response.data);
            })
            .catch((error) => {
                showSnackbar('Failed to fetch course', 'error');
            });
    };

    const handleCourseInfoRequest = () => {
        fetchCourseInfo(parseInt(courseId, 10))
            .then((response) => {
                setCoursePlan(response.data.courseInfos[0]);
            })
            .catch((error) => {
                showSnackbar('Failed to fetch course info.', 'error');
            });
    };

    const handleDeleteMessage = (id: number) => {
        deleteMessage(parseInt(courseId, 10), parseInt(chatId, 10), id)
            .then(() => {
                handleFetchMessages();
            })
            .catch((error) => {
                showSnackbar('Failed to delete message', 'error');
            });
    };

    const handleCategoryChange = (categoryId: number) => {
        setSelectedSailCategoryIds((prev) => {
            if (prev.includes(categoryId)) {
                return prev.filter((id) => id !== categoryId);
            } else if (prev.length < 3) {
                return [...prev, categoryId];
            } else {
                return prev;
            }
        });
    };

    const handleFetchCourseChats = () => {
        fetchCourseChats(parseInt(courseId, 10))
            .then((response) => {
                const filteredChats = response.data.chats.filter((chat: Chat) => chat.modeId === EVALUATION_MODE_ID);
                const matchingChat = response.data.chats.find((chat: Chat) => chat.id === parseInt(chatId, 10));
                setChat(matchingChat);
                setEvaluationChatId(filteredChats[0]?.id || 0);
                if (matchingChat?.modeId === 1) {
                    // Holistic mode: select all categories and send initial message
                    setSelectedSailCategoryIds(sailCategories.map(category => category.id));
                    setConfirmedCategories(true);
                    sendInitialMessage();
                }
            })
            .catch((error) => {
                showSnackbar('Failed to fetch Evaluation chat', 'error');
            });
    };

    const handleAddChatCategories = async () => {
        if (selectedSailCategoryIds.length === 0) {
            return;
        }
        setLoading(true);
        try {
            await addChatCategories(parseInt(courseId, 10), parseInt(chatId, 10), { sailCategoryIds: selectedSailCategoryIds });
        } catch (error) {
            showSnackbar('Failed to add SAIL categories', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateMessage = async (messageId: number, inputText?: string): Promise<void> => {
        // Add the message ID to loadingMessages to indicate it's being updated
        setLoadingMessages((prev) => [...prev, messageId]);

        // Construct parameters based on whether inputText is provided
        const params: { llmId: number; inputText?: string } = {
            llmId: selectedLlm,
        };

        if (inputText !== undefined && inputText !== null) {
            params.inputText = inputText;
        }

        try {
            // Make API call to update the message
            await updateMessage(parseInt(courseId, 10), parseInt(chatId, 10), messageId, params);
            // Fetch updated messages
            await handleFetchMessages();
        } catch (error) {
            showSnackbar('Failed to update message', 'error');
        } finally {
            // Remove the message ID from loadingMessages regardless of success or failure
            setLoadingMessages((prev) => prev.filter((id) => id !== messageId));
        }
    };

    const handleAddMessage = (params: any, tempId?: number) => {
        return addMessage(parseInt(courseId, 10), parseInt(chatId, 10), params)
            .then(() => {
                handleFetchMessages();
                setInput("");
                if (tempId) {
                    setLoadingMessages((prev) => prev.filter((id) => id !== tempId));
                }
            })
            .catch((error) => {
                showSnackbar('Failed to add message', 'error');
                if (tempId) {
                    setLoadingMessages((prev) => prev.filter((id) => id !== tempId));
                    setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
                }
                throw error;
            });
    };

    const sendInitialMessage = async () => {
        if (!coursePlan || !course || messages.length > 0) return;
        setInitialLoading(true);
        try {
            await handleAddChatCategories();
            const params = {
                inputText: `Based on my course information, what suggestions would you have for embedding AI literacy into my course design?`,
                llmId: selectedLlm,
            };
            await handleAddMessage(params);
            setConfirmedCategories(true);
        } catch (error) {
            showSnackbar('Failed to send initial messages', 'error');
        } finally {
            setInitialLoading(false);
        }
    };

    const sendMessage = async () => {
        if (!input.trim()) return;
        const tempId = generateTempId();
        const tempMessage: Message = {
            id: tempId,
            chatId: parseInt(chatId, 10),
            inputText: input,
            responseText: "",
            createdAt: new Date(),
        };

        setMessages((prev) => [...prev, tempMessage]);
        setLoadingMessages((prev) => [...prev, tempId]);

        try {
            const params = {
                inputText: input,
                llmId: selectedLlm,
            };
            await handleAddMessage(params, tempId);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleConfirmCategories = () => {
        setConfirmedCategories(true);
        sendInitialMessage();
    };

    const handleLlmSelect = (llmId: number) => {
        setSelectedLlm(llmId);
        handleMenuClose();
    };

    const handleBack = () => {
        navigate(`/courses/${courseId}/chat/evaluation/${evaluationChatId}`, {replace: true})
    }

    useEffect(() => {
        handleFetchConversation();
        handleCourseInfoRequest();
        handleFetchMessages();
        handleFetchCourseChats();
        handleFetchLLMs();
    }, []);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    useEffect(() => {
        if (confirmedCategories && !initialLoading) {
            sendInitialMessage();
        }
    }, [confirmedCategories]);

    const messageFeed = () => {
        if (initialLoading && messages.length === 0) {
            return (
                <MessageArray
                    key={tempInitialMessage.id}
                    message={tempInitialMessage}
                    deleteMessage={handleDeleteMessage}
                    handleUpdateMessage={handleUpdateMessage}
                    loading={true}
                    disableEditDelete={true}
                />
            );
        }
        if (!messages) return <>Loading...</>;
        if (messages.length === 0 && isVisible && chat?.modeId === 2) {
            return (
                <Box sx={{mt: 2, textAlign: 'right'}}>
                    <Chip
                        variant="outlined"
                        color="primary"
                        onClick={handleConfirmCategories}
                        label="Confirm Categories"
                        disabled={selectedSailCategoryIds.length === 0 || loading || initialLoading}
                    />
                </Box>
            );
        };
        return (
            <>
                {messages.map((message: Message, index: number) => (
                    <MessageArray
                        key={message.id}
                        message={message}
                        deleteMessage={handleDeleteMessage}
                        handleUpdateMessage={handleUpdateMessage}
                        loading={loadingMessages.includes(message.id)}
                        disableEditDelete={index === 0}
                    />
                ))}
            </>
        );
    };

    const llmMenu = () => {
        if (!llmList || llmList.length === 0) return <MenuItem disabled>No LLMs available</MenuItem>;
        return (
            <>
                {llmList.map((llm) => (
                    <MenuItem
                        key={llm.id}
                        onClick={() => handleLlmSelect(llm.id)}
                        selected={llm.id === selectedLlm}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {llm.icon}
                            <Box>
                                <Typography variant="body1">{llm.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {llm.description}
                                </Typography>
                            </Box>
                        </Box>
                    </MenuItem>
                ))}
            </>
        );
    };

    const getSelectedLlmName = () => {
        if (!llmList || llmList.length === 0) return "Select LLM";
        const selected = llmList.find((llm) => llm.id === selectedLlm);
        return selected ? selected.name : "Select LLM";
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100vh",
            }}
        >
            <Container
                maxWidth="lg"
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    height: "100%",
                    overflowX: 'visible',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        overflowX: 'visible',
                        maxHeight: "calc(100% - 100px)", // Leaves space for input bar
                        mt: 5,
                    }}
                >
                    <Typography variant="h4" gutterBottom sx={{ textAlign: "center", mb: 4 }}>
                        {`${course?.courseName || "Loading..."} AI Literacy Suggestions Chat`}
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom sx={{ textAlign: "center", mb: 4 }}>
                        {chat?.modeId === 1
                            ? localization.suggestionChat.instructions.holistic
                            : localization.suggestionChat.instructions.digest}
                    </Typography>
                    {isVisible && chat?.modeId === 2 && (
                        <Grid container spacing={2} sx={{ mb: 2, ...transitionStyles }}>
                            {sailCategories.map((category) => (
                                <Grid size={6} key={category.id}>
                                    <Card
                                        sx={{
                                            backgroundColor: selectedSailCategoryIds.includes(category.id)
                                                ? 'primary.main'
                                                : 'background.paper',
                                            color: selectedSailCategoryIds.includes(category.id)
                                                ? 'primary.contrastText'
                                                : 'text.primary',
                                            transition: 'background-color 0.3s, color 0.3s',
                                            '&:hover': {
                                                backgroundColor: selectedSailCategoryIds.includes(category.id)
                                                    ? 'primary.dark'
                                                    : 'action.hover',
                                            },
                                        }}
                                    >
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Checkbox
                                                    checked={selectedSailCategoryIds.includes(category.id)}
                                                    onChange={() => handleCategoryChange(category.id)}
                                                    disabled={loading || (!selectedSailCategoryIds.includes(category.id) && selectedSailCategoryIds.length >= 3)}
                                                    aria-label={`Select ${category.name} category`}
                                                    sx={{
                                                        color: selectedSailCategoryIds.includes(category.id)
                                                            ? 'primary.contrastText'
                                                            : 'primary.main',
                                                        '&.Mui-checked': {
                                                            color: selectedSailCategoryIds.includes(category.id)
                                                                ? 'primary.contrastText'
                                                                : 'primary.main',
                                                        },
                                                    }}
                                                />
                                                <Typography variant="h6">{category.name}</Typography>
                                            </Box>
                                            <Typography variant="body2">{category.description}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid> ))}
                        </Grid>
                    )}
                    {messageFeed()}
                    <Box ref={scrollRef} />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '120%', // Responsive to parent
                        maxWidth: 'lg', // Matches Material-UI's lg breakpoint
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)', // Centers Box relative to parent
                        bottom: 0, // Anchors to bottom of Container
                        zIndex: 1100, // Ensures Box is above other elements
                        pb: 3,
                    }}
                >
                    <Button
                        color="primary"
                        startIcon={<NavigateBeforeIcon />}
                        onClick={handleBack}
                    >
                        Evaluation Mode
                    </Button>
                    <TextField
                        sx={{
                            bgcolor: "background.secondaryPaper",
                            borderRadius: "4px",
                            zIndex: 1050,
                            width: '100%', // Fills the Box width up to maxWidth
                            maxWidth: '960px', // Caps TextField at md width
                        }}
                        variant="outlined"
                        multiline
                        minRows={1}
                        maxRows={5}
                        placeholder="Do you have any follow-up questions?"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Button
                                            variant="text"
                                            size="medium"
                                            onClick={handleMenuOpen}
                                            endIcon={
                                                <ArrowDropDownIcon
                                                    sx={{
                                                        transform: Boolean(anchorEl) ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.2s ease-in-out',
                                                    }}
                                                />
                                            }
                                        >
                                            {getSelectedLlmName()}
                                        </Button>
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            color="primary"
                                            onClick={sendMessage}
                                            disabled={loading || initialLoading || loadingMessages.length > 0 || !input.trim()}
                                        >
                                            {loading || initialLoading || loadingMessages.length > 0 ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <SendIcon />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <Menu
                        id="llm-menu"
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                    >
                        {llmMenu()}
                    </Menu>
                    {/*<Button*/}
                    {/*    color="primary"*/}
                    {/*    endIcon={<NavigateNextIcon />}*/}
                    {/*    // onClick={handleOpenChatDialog}*/}
                    {/*>*/}
                    {/*    Goto Suggestions*/}
                    {/*</Button>*/}
                </Box>
            </Container>
        </Box>
    );
};

export default SuggestionPage;